import React, { useCallback, useEffect, useRef, useState } from 'react';

import { SortableContext, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import { Row } from '@tanstack/react-table';

import { WithSubData } from './DataDisplayTable';
import { RenderCell } from './renderCell';

interface RenderRowProps<T> {
  row: Row<WithSubData<T>>;
  isLightRow: boolean;
  isSelected: boolean;
  isExpanded: boolean;
  width: number;
  isDragging: string;
  rowFirstColumnClasses: string;
  columnOrder: string[];
  stylingOptions: {
    rowColor?: string | undefined;
    alternateRowColor?: string | undefined;
    subRowColor?: string | undefined;
    alternateLightRows: boolean;
    hoverRowColor?: 'gray' | 'none' | 'brand_dark';
    selectedRowColor?: string | undefined;
  };
  renderExpandedRow?: (row: Row<WithSubData<T>>) => React.ReactNode;
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  compact?: boolean;
}

const RenderRowComponent = <T,>({
  scrollContainerRef,
  row,
  isLightRow,
  stylingOptions,
  isSelected,
  columnOrder,
  width,
  isDragging,
  isExpanded,
  renderExpandedRow,
  rowFirstColumnClasses,
  compact = false,
}: RenderRowProps<T>) => {
  const rowColor =
    row.depth > 0
      ? stylingOptions.subRowColor
      : isLightRow
        ? stylingOptions.alternateRowColor
        : stylingOptions.rowColor;

  const handleRowClick = (event: React.MouseEvent<HTMLTableRowElement>) => {
    if ((event.target as HTMLElement).tagName.toLowerCase() !== 'input') {
      row.getToggleExpandedHandler()();
    }
  };

  const getRowKey = useCallback((row: Row<WithSubData<T>>) => {
    const originalData = row.original;
    // Create a string that combines the row id and a hash of the row's values
    const dataHash = Object.values(originalData).join('|');
    return `${row.id}-${dataHash}`;
  }, []);

  const bgColor = isSelected
    ? stylingOptions?.selectedRowColor
      ? stylingOptions?.selectedRowColor
      : 'bg-slate-600'
    : rowColor
      ? rowColor
      : row.depth > 0
        ? 'bg-background-canvas'
        : isLightRow
          ? 'bg-gray-950'
          : 'bg-background-canvas';

  const [leftOffset, setLeftOffset] = useState(0);

  const rowClassNames = `border-t border-gray-900 group relative ${stylingOptions.hoverRowColor && `hover:${stylingOptions.hoverRowColor}`}`;
  const rowRef = useRef<HTMLTableRowElement | null>(null);

  useEffect(() => {
    const scrollEl = scrollContainerRef.current;
    const rowEL = rowRef.current;
    if (!scrollEl) return;
    if (!rowEL) return;

    const handleScroll = () => {
      const scrollBounds = scrollEl.getBoundingClientRect();
      const rowBounds = rowEL.getBoundingClientRect();

      const scrollRightX = scrollBounds.left + scrollEl.clientWidth;
      const rowRightX = rowBounds.right;

      // The distance the row’s right edge is outside the visible scroll area
      const overflowRight = rowRightX - scrollRightX;

      // So shift the absolute element **left** by that much
      const shiftLeft = Math.max(0, overflowRight); // Don't go negative

      setLeftOffset(shiftLeft);
    };
    setTimeout(() => requestAnimationFrame(handleScroll), 0);

    scrollEl.addEventListener('scroll', handleScroll);
    return () => scrollEl.removeEventListener('scroll', handleScroll);
  }, [rowRef, scrollContainerRef]);

  return (
    <>
      <SortableContext items={columnOrder} strategy={horizontalListSortingStrategy}>
        <tr ref={rowRef} key={getRowKey(row)} onClick={handleRowClick} className={rowClassNames}>
          {row.getVisibleCells().map((cell, idx) => (
            <RenderCell
              hoverLeftOffset={leftOffset}
              cell={cell}
              columnOrder={columnOrder}
              key={cell.id}
              width={width}
              bgColor={bgColor}
              isColumnResize={isDragging}
              extraClasses={idx === 0 ? rowFirstColumnClasses : ''}
              hoverRowColor={stylingOptions?.hoverRowColor}
              compact={compact}
            />
          ))}
        </tr>
      </SortableContext>
      {isExpanded && renderExpandedRow && (
        <tr>
          <td
            colSpan={row.getVisibleCells().length}
            className={`bg-gray-1000 ${rowFirstColumnClasses}`}
          >
            {renderExpandedRow(row)}
          </td>
        </tr>
      )}
    </>
  );
};

export const RenderRow = React.memo(RenderRowComponent) as typeof RenderRowComponent;
